import { useState } from 'react'
import { Button, Checkbox, Drawer, Select } from '@mantine/core'
import { DatePickerInput } from '@mantine/dates'
import dayjs from 'dayjs'
import {
  ACTION_TYPES,
  FOLLOWUP_STATUSES,
  JOURNEY_STATUS_FILTERS,
  ORDER_STATUSES,
  VISIT_TYPES,
} from '@behuman/core-contract'
import type { CareJourneysData, JourneyFilters } from './journeys'

// Presets the date-range select offers, in days back from today. 'custom' opens
// the two date pickers; 'all' clears the range entirely.
const RANGE_PRESETS = [
  { value: '7', label: 'Last 7 days' },
  { value: '30', label: 'Last 30 days' },
  { value: '90', label: 'Last 90 days' },
  { value: 'all', label: 'All time' },
  { value: 'custom', label: 'Custom range' },
]

/** Must match `DEFAULT_RANGE_DAYS` in `care-journeys.handler.ts`. */
const DEFAULT_RANGE = '7'

/** Lower bound for "All time" — comfortably older than any clinical record here. */
const ALL_TIME_FROM = '1900-01-01T00:00:00.000Z'

/** Title-case a kebab enum value for display, e.g. `not-sent` → `Not sent`. */
function humanize(value: string): string {
  const spaced = value.replace(/-/g, ' ')
  return spaced.charAt(0).toUpperCase() + spaced.slice(1)
}

function options(values: readonly string[], allLabel: string) {
  return [
    { value: '', label: allLabel },
    ...values.map((value) => ({ value, label: humanize(value) })),
  ]
}

/** Toggle chip colours for the Status row. Text always present, never colour alone. */
const STATUS_CHIP: Record<string, { on: string; off: string }> = {
  open: { on: 'border-gray-500 bg-gray-100 text-gray-800', off: 'border-gray-300 text-gray-600' },
  overdue: { on: 'border-red-400 bg-red-50 text-red-700', off: 'border-gray-300 text-gray-600' },
  future: { on: 'border-blue-400 bg-blue-50 text-blue-700', off: 'border-gray-300 text-gray-600' },
  completed: {
    on: 'border-emerald-400 bg-emerald-50 text-emerald-700',
    off: 'border-gray-300 text-gray-600',
  },
}

type Draft = {
  range: string
  customFrom: Date | null
  customTo: Date | null
  practitionerId: string
  ownerId: string
  statuses: string[]
  actionType: string
  orderStatus: string
  followUpStatus: string
  visitType: string
  needsAttentionOnly: boolean
}

const EMPTY_DRAFT: Draft = {
  range: DEFAULT_RANGE,
  customFrom: null,
  customTo: null,
  practitionerId: '',
  ownerId: '',
  statuses: [],
  actionType: '',
  orderStatus: '',
  followUpStatus: '',
  visitType: '',
  needsAttentionOnly: false,
}

function draftToFilters(draft: Draft): JourneyFilters {
  const one = <T extends string>(value: string): T[] | undefined =>
    value ? ([value] as T[]) : undefined

  let dateFrom: string | undefined
  let dateTo: string | undefined
  if (draft.range === 'custom') {
    dateFrom = draft.customFrom ? dayjs(draft.customFrom).startOf('day').toISOString() : undefined
    dateTo = draft.customTo ? dayjs(draft.customTo).endOf('day').toISOString() : undefined
  } else if (draft.range === 'all') {
    // Must be sent explicitly. Omitting it would fall through to the handler's
    // own default window, making "All time" a silent no-op.
    dateFrom = ALL_TIME_FROM
    dateTo = dayjs().endOf('day').toISOString()
  } else if (draft.range !== DEFAULT_RANGE) {
    dateFrom = dayjs().subtract(Number(draft.range), 'day').startOf('day').toISOString()
    dateTo = dayjs().endOf('day').toISOString()
  }
  // The default range is deliberately left off the wire: the handler already
  // defaults to the same window, so sending it changes nothing except to
  // inflate the "active filters" badge by two on an untouched dashboard.

  return {
    ...(dateFrom ? { dateFrom } : {}),
    ...(dateTo ? { dateTo } : {}),
    ...(draft.practitionerId ? { practitionerIds: [draft.practitionerId] } : {}),
    ...(draft.ownerId ? { ownerIds: [draft.ownerId] } : {}),
    ...(draft.statuses.length
      ? { statuses: draft.statuses as NonNullable<JourneyFilters>['statuses'] }
      : {}),
    ...(draft.actionType
      ? {
          actionTypes: one<NonNullable<NonNullable<JourneyFilters>['actionTypes']>[number]>(
            draft.actionType,
          ),
        }
      : {}),
    ...(draft.orderStatus
      ? {
          orderStatuses: one<NonNullable<NonNullable<JourneyFilters>['orderStatuses']>[number]>(
            draft.orderStatus,
          ),
        }
      : {}),
    ...(draft.followUpStatus
      ? {
          followUpStatuses: one<
            NonNullable<NonNullable<JourneyFilters>['followUpStatuses']>[number]
          >(draft.followUpStatus),
        }
      : {}),
    ...(draft.visitType
      ? {
          visitTypes: one<NonNullable<NonNullable<JourneyFilters>['visitTypes']>[number]>(
            draft.visitType,
          ),
        }
      : {}),
    ...(draft.needsAttentionOnly ? { needsAttentionOnly: true } : {}),
  }
}

const idOf = (reference: string) => reference.split('/')[1] ?? reference

const dayValue = (date: Date | null) => (date ? date.getTime() : null)

/**
 * Draft equality, so the footer buttons can reflect real state rather than
 * being permanently clickable (PD-1467). Every field is a primitive except the
 * two dates and the status list, so a hand-written comparison is cheaper and
 * clearer here than pulling in a deep-equal dependency.
 */
function sameDraft(a: Draft, b: Draft): boolean {
  return (
    a.range === b.range &&
    dayValue(a.customFrom) === dayValue(b.customFrom) &&
    dayValue(a.customTo) === dayValue(b.customTo) &&
    a.practitionerId === b.practitionerId &&
    a.ownerId === b.ownerId &&
    a.statuses.length === b.statuses.length &&
    a.statuses.every((status) => b.statuses.includes(status)) &&
    a.actionType === b.actionType &&
    a.orderStatus === b.orderStatus &&
    a.followUpStatus === b.followUpStatus &&
    a.visitType === b.visitType &&
    a.needsAttentionOnly === b.needsAttentionOnly
  )
}

export const JourneyFilterDrawer = ({
  opened,
  onClose,
  filterOptions,
  onApply,
}: {
  opened: boolean
  onClose: () => void
  filterOptions: CareJourneysData['filterOptions'] | undefined
  onApply: (filters: JourneyFilters) => void
}) => {
  // Edits live here uncommitted until Apply, which is what makes "closing the
  // drawer without applying does not change results" true: `onApply` is the only
  // path that reaches the dashboard.
  const [draft, setDraft] = useState<Draft>(EMPTY_DRAFT)
  // What the dashboard is currently showing. Kept alongside the draft so the
  // footer can tell "edited but not applied" from "already applied" — without
  // it, Apply has nothing to compare against and stays live forever (PD-1467).
  const [applied, setApplied] = useState<Draft>(EMPTY_DRAFT)

  const set = <K extends keyof Draft>(key: K, value: Draft[K]) =>
    setDraft((current) => ({ ...current, [key]: value }))

  // Nothing to apply until an edit actually diverges from what is on screen.
  const canApply = !sameDraft(draft, applied)
  // Enabled while there is anything to clear, whether it is applied to the
  // board or only sitting unapplied in the form.
  const canClear = !sameDraft(draft, EMPTY_DRAFT) || !sameDraft(applied, EMPTY_DRAFT)

  const toggleStatus = (status: string) =>
    setDraft((current) => ({
      ...current,
      statuses: current.statuses.includes(status)
        ? current.statuses.filter((value) => value !== status)
        : [...current.statuses, status],
    }))

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size={440}
      title={<span className="text-xl font-semibold">Filters</span>}
    >
      <div className="flex flex-col gap-4">
        <Select
          label="Date range"
          data={RANGE_PRESETS}
          value={draft.range}
          onChange={(value) => set('range', value ?? DEFAULT_RANGE)}
          allowDeselect={false}
        />

        {draft.range === 'custom' ? (
          <div className="grid grid-cols-2 gap-3">
            {/* Each bound caps the other, so an inverted range can't be picked
                in the first place (PD-1468). Whichever date is chosen first
                disables the impossible days in the other calendar, which is
                cheaper for the user than accepting the input and then
                explaining it was wrong. */}
            <DatePickerInput
              label="From"
              placeholder="MM/DD/YYYY"
              valueFormat="MM/DD/YYYY"
              value={draft.customFrom}
              maxDate={draft.customTo ?? undefined}
              onChange={(value) => set('customFrom', value as Date | null)}
            />
            <DatePickerInput
              label="To"
              placeholder="MM/DD/YYYY"
              valueFormat="MM/DD/YYYY"
              value={draft.customTo}
              minDate={draft.customFrom ?? undefined}
              onChange={(value) => set('customTo', value as Date | null)}
            />
          </div>
        ) : null}

        {/* Patient is deliberately NOT here — it lives in the toolbar as
            `JourneyPatientSelect`. Finding one patient is the most common thing
            a coordinator does on this board, and behind Filters it cost a drawer
            open, a search, an Apply and a close. */}

        <div className="grid grid-cols-2 gap-3">
          <Select
            label="Practitioner"
            data={[
              { value: '', label: 'All practitioners' },
              ...(filterOptions?.practitioners ?? []).map((person) => ({
                value: idOf(person.reference),
                label: person.display,
              })),
            ]}
            value={draft.practitionerId}
            onChange={(value) => set('practitionerId', value ?? '')}
            allowDeselect={false}
          />
          <Select
            label="Care Coordinator / Owner"
            data={[
              { value: '', label: 'All owners' },
              ...(filterOptions?.owners ?? []).map((person) => ({
                value: idOf(person.reference),
                label: person.display,
              })),
            ]}
            value={draft.ownerId}
            onChange={(value) => set('ownerId', value ?? '')}
            allowDeselect={false}
          />
        </div>

        <div>
          <p className="mb-1.5 text-sm font-medium text-gray-900">Status</p>
          <div className="flex flex-wrap gap-2">
            {JOURNEY_STATUS_FILTERS.map((status) => {
              const on = draft.statuses.includes(status)
              return (
                <button
                  key={status}
                  type="button"
                  aria-pressed={on}
                  onClick={() => toggleStatus(status)}
                  className={`rounded-md border px-3 py-1.5 text-sm ${
                    on ? STATUS_CHIP[status].on : STATUS_CHIP[status].off
                  }`}
                >
                  {humanize(status)}
                </button>
              )
            })}
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Select
            label="Action type"
            description="Lab, Imaging, Referral, Follow-up Visit"
            // Mantine puts the description above the input by default, which
            // wraps to two lines here and pushes this control out of alignment
            // with "Order status" beside it. The approved design puts it below.
            inputWrapperOrder={['label', 'input', 'description', 'error']}
            data={options(ACTION_TYPES, 'All action types')}
            value={draft.actionType}
            onChange={(value) => set('actionType', value ?? '')}
            allowDeselect={false}
          />
          <Select
            label="Order status"
            data={options(ORDER_STATUSES, 'All order statuses')}
            value={draft.orderStatus}
            onChange={(value) => set('orderStatus', value ?? '')}
            allowDeselect={false}
          />
        </div>

        <div className="grid grid-cols-2 gap-3">
          <Select
            label="Follow-up status"
            data={options(FOLLOWUP_STATUSES, 'All follow-up statuses')}
            value={draft.followUpStatus}
            onChange={(value) => set('followUpStatus', value ?? '')}
            allowDeselect={false}
          />
          <Select
            label="Visit type"
            data={options(VISIT_TYPES, 'All visit types')}
            value={draft.visitType}
            onChange={(value) => set('visitType', value ?? '')}
            allowDeselect={false}
          />
        </div>

        <Checkbox
          label="Show only patients needing attention"
          description="Overdue orders, missing results, or unscheduled follow-ups"
          checked={draft.needsAttentionOnly}
          onChange={(event) => set('needsAttentionOnly', event.currentTarget.checked)}
        />

        <div className="mt-2 flex items-center justify-between border-t border-gray-200 pt-4">
          <Button
            variant="subtle"
            disabled={!canClear}
            onClick={() => {
              setDraft(EMPTY_DRAFT)
              setApplied(EMPTY_DRAFT)
              onApply(draftToFilters(EMPTY_DRAFT))
            }}
          >
            Clear all
          </Button>
          <Button
            disabled={!canApply}
            onClick={() => {
              setApplied(draft)
              onApply(draftToFilters(draft))
              onClose()
            }}
          >
            Apply filters
          </Button>
        </div>
      </div>
    </Drawer>
  )
}
